import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, adminAuth } from "../lib/api";
import { NaaviLogo } from "../components/brand/Shapes";
import { toast } from "sonner";
import { MapPin, LogOut, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

// Public staff clock-in/out page reached by scanning the wall QR.
// Requires a valid admin (=staff) JWT; if missing, redirects to /admin/login.
export default function StaffClock() {
  const nav = useNavigate();
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [geo, setGeo] = useState(null); // {lat, lng, accuracy_m, status: 'ok'|'denied'|'pending'|'noinfo', error?}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!adminAuth.get()) { nav("/admin/login?next=/staff/clock"); return; }
    adminApi.get("/staff/today")
      .then((res) => setToday(res.data))
      .catch((err) => {
        if (err?.response?.status === 401) nav("/admin/login?next=/staff/clock");
        else toast.error(err?.response?.data?.detail || "Failed to load");
      })
      .finally(() => setLoading(false));
  }, [nav]);

  const requestGeo = () => {
    if (!navigator.geolocation) {
      setGeo({ status: "noinfo", error: "GPS not supported on this device." });
      return;
    }
    setGeo({ status: "pending" });
    navigator.geolocation.getCurrentPosition(
      (pos) => setGeo({
        status: "ok",
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy_m: pos.coords.accuracy,
      }),
      (err) => setGeo({ status: "denied", error: err.message }),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  // School distance preview
  const distance = (() => {
    if (geo?.status !== "ok" || !today?.settings?.school_lat) return null;
    const { school_lat: lat2, school_lng: lng2 } = today.settings;
    const toRad = (d) => d * Math.PI / 180;
    const R = 6371000;
    const dphi = toRad(lat2 - geo.lat);
    const dlam = toRad(lng2 - geo.lng);
    const a = Math.sin(dphi/2)**2 + Math.cos(toRad(geo.lat)) * Math.cos(toRad(lat2)) * Math.sin(dlam/2)**2;
    return Math.round(2 * R * Math.asin(Math.sqrt(a)));
  })();

  const geofence = today?.settings?.geofence_m || 50;
  const maxAcc = today?.settings?.max_accuracy_m || 100;
  const insideFence = distance !== null && distance <= geofence;
  const accuracyOk = geo?.accuracy_m != null && geo.accuracy_m <= maxAcc;
  const canClock = geo?.status === "ok" && insideFence && accuracyOk;

  const doClock = async (type) => {
    if (!canClock) return;
    setBusy(true);
    try {
      const res = await adminApi.post("/staff/clock", {
        type,
        lat: geo.lat,
        lng: geo.lng,
        accuracy_m: geo.accuracy_m,
      });
      toast.success(res.data.status === "already_marked" ? `Already clocked ${type}` : `Clocked ${type} successfully`);
      const refreshed = await adminApi.get("/staff/today");
      setToday(refreshed.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Clock-in failed");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => { adminAuth.clear(); nav("/admin/login"); };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] grid place-items-center">
        <Loader2 className="animate-spin text-[#0F1B47]" size={28} />
      </div>
    );
  }

  if (!today) return null;

  const { clock_in, clock_out, settings, me } = today;
  const schoolReady = settings?.school_lat != null && settings?.school_lng != null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDFDF9] to-[#F5F5F0] flex flex-col">
      {/* Header */}
      <header className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <NaaviLogo className="w-9 h-9" />
          <div>
            <div className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-[#648DE5]">
              {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" })}
            </div>
            <div className="font-display text-[18px] text-[#0F1B47] font-medium leading-tight">Hello, {me?.name || me?.email}</div>
          </div>
        </div>
        <button onClick={logout} className="text-[#0F1B47] p-2 rounded-full hover:bg-[#0F1B47]/5" aria-label="Sign out" data-testid="staff-clock-logout">
          <LogOut size={18} />
        </button>
      </header>

      <main className="flex-1 px-5 pb-8 max-w-md mx-auto w-full">
        {/* Setup not done */}
        {!schoolReady && (
          <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded-2xl px-4 py-3 text-sm mb-4 flex gap-2" data-testid="staff-clock-noschool">
            <AlertTriangle size={16} className="mt-0.5 flex-none" />
            <div>
              School location hasn&apos;t been set yet. Ask the superadmin to set it from <b>Admin → School settings</b>, then come back here.
            </div>
          </div>
        )}

        {/* Already-clocked summary */}
        {(clock_in || clock_out) && (
          <div className="bg-[#FDFDF9] rounded-2xl p-4 mb-4 shadow-sm border border-[#0F1B47]/8" data-testid="staff-clock-status">
            <div className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-[#648DE5] mb-2">Today</div>
            <div className="grid grid-cols-2 gap-3">
              <SlotPill label="Clock-in" record={clock_in} cutoff={settings?.on_time_cutoff} />
              <SlotPill label="Clock-out" record={clock_out} />
            </div>
          </div>
        )}

        {/* Location request */}
        {schoolReady && !(clock_in && clock_out) && (
          <div className="bg-[#FDFDF9] rounded-3xl p-5 shadow-sm border border-[#0F1B47]/8">
            <div className="flex items-center gap-2 mb-1">
              <MapPin size={16} className="text-[#0F1B47]" />
              <span className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-[#648DE5]">Step 1 of 2</span>
            </div>
            <h2 className="font-display text-[24px] text-[#0F1B47] font-medium leading-tight mb-2">
              Confirm you&apos;re at the campus
            </h2>
            <p className="text-[13px] text-[#4A5568] leading-[1.55] mb-3">
              Stand within <b>{geofence} m</b> of the campus. Your phone&apos;s GPS verifies you&apos;re on-site.
            </p>

            {!geo && (
              <button
                type="button"
                onClick={requestGeo}
                className="w-full bg-[#0F1B47] text-[#FDFDF9] rounded-2xl py-3 font-bold text-[14px] active:scale-[0.98] transition"
                data-testid="staff-clock-request-geo"
              >
                Use my location
              </button>
            )}
            {geo?.status === "pending" && (
              <div className="text-[13px] text-[#718096] inline-flex items-center gap-2">
                <Loader2 className="animate-spin" size={14} /> Reading GPS…
              </div>
            )}
            {geo?.status === "denied" && (
              <div className="bg-[#FED7D7] text-[#C53030] rounded-2xl p-3 text-[12px]">
                Location denied. Enable location for this site in your browser settings, then tap <b>Use my location</b> again.
                <button type="button" onClick={requestGeo} className="block mt-2 underline font-bold">Retry</button>
              </div>
            )}
            {geo?.status === "noinfo" && (
              <div className="bg-[#FED7D7] text-[#C53030] rounded-2xl p-3 text-[12px]">{geo.error}</div>
            )}
            {geo?.status === "ok" && (
              <>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <Stat
                    label="Distance"
                    value={`${distance} m`}
                    accent={insideFence ? "ok" : "bad"}
                    note={insideFence ? "✓ On site" : `Too far (>${geofence} m)`}
                  />
                  <Stat
                    label="Accuracy"
                    value={`±${Math.round(geo.accuracy_m)} m`}
                    accent={accuracyOk ? "ok" : "bad"}
                    note={accuracyOk ? "Good signal" : `Too noisy (>${maxAcc} m)`}
                  />
                </div>
                <div className="mt-4 flex gap-2 flex-wrap">
                  {!clock_in && (
                    <button
                      type="button"
                      disabled={!canClock || busy}
                      onClick={() => doClock("in")}
                      className="flex-1 bg-[#C8DF52] text-[#0F1B47] rounded-2xl py-3 font-bold text-[14px] disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition"
                      data-testid="staff-clock-in-btn"
                    >
                      {busy ? "…" : "Clock in"}
                    </button>
                  )}
                  {clock_in && !clock_out && (
                    <button
                      type="button"
                      disabled={!canClock || busy}
                      onClick={() => doClock("out")}
                      className="flex-1 bg-[#0F1B47] text-[#FDFDF9] rounded-2xl py-3 font-bold text-[14px] disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition"
                      data-testid="staff-clock-out-btn"
                    >
                      {busy ? "…" : "Clock out"}
                    </button>
                  )}
                  <button type="button" onClick={requestGeo} className="px-4 rounded-2xl border border-[#0F1B47]/15 text-[13px] font-semibold text-[#0F1B47]">
                    Re-check
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {clock_in && clock_out && (
          <div className="bg-[#E6FFFA] text-[#2F855A] rounded-3xl p-5 mt-3 text-center" data-testid="staff-clock-done">
            <CheckCircle2 size={32} className="mx-auto mb-2" />
            <div className="font-display text-[22px] font-medium">All done for today</div>
            <p className="text-[13px] mt-1">See you tomorrow.</p>
          </div>
        )}

        <div className="text-center text-[10px] text-[#A0AEC0] mt-6 leading-relaxed">
          Clock-in window: {settings?.clock_in_start} – {settings?.clock_in_end}<br/>
          Clock-out window: {settings?.clock_out_start} – {settings?.clock_out_end}
        </div>
      </main>
    </div>
  );
}

function SlotPill({ label, record, cutoff }) {
  if (!record) {
    return (
      <div className="bg-[#F5F5F0] rounded-2xl p-3">
        <div className="text-[9px] font-extrabold tracking-[0.18em] uppercase text-[#A0AEC0]">{label}</div>
        <div className="text-[15px] text-[#A0AEC0] font-bold mt-1">—</div>
      </div>
    );
  }
  const t = new Date(record.timestamp);
  const time = t.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const late = record.status === "late";
  return (
    <div className={`rounded-2xl p-3 ${late ? "bg-[#FFF5B7]" : "bg-[#E6FFFA]"}`}>
      <div className={`text-[9px] font-extrabold tracking-[0.18em] uppercase ${late ? "text-[#B7791F]" : "text-[#2F855A]"}`}>{label}</div>
      <div className={`text-[15px] font-bold mt-1 ${late ? "text-[#B7791F]" : "text-[#2F855A]"}`}>{time}</div>
      <div className="text-[10px] text-[#4A5568] mt-0.5">
        {record.manual ? "Marked manually" : late ? `After ${cutoff}` : "On time"}
      </div>
    </div>
  );
}

function Stat({ label, value, note, accent }) {
  const accents = {
    ok: "bg-[#E6FFFA] text-[#2F855A]",
    bad: "bg-[#FED7D7] text-[#C53030]",
  };
  return (
    <div className="bg-[#F5F5F0] rounded-2xl p-3">
      <div className="text-[9px] font-extrabold tracking-[0.18em] uppercase text-[#A0AEC0]">{label}</div>
      <div className="text-[16px] text-[#0F1B47] font-bold mt-1">{value}</div>
      <div className={`text-[10px] mt-1 inline-block px-2 py-0.5 rounded-full font-bold ${accents[accent] || ""}`}>{note}</div>
    </div>
  );
}
